import { useEffect, useState } from "react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { PageHeader, Th } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogIn, Building, Upload, X } from "lucide-react";
import { LimitsBar, LIMIT_META } from "@/components/LimitsBar";

const EMPTY_LIMITS = { max_users: 0, max_channels: 0, max_connections: 0, max_sectors: 0, max_os: 0 };
const EMPTY = { razao_social: "", nome_fantasia: "", cnpj: "", phone: "", email: "", address: "", city: "", state: "", logo: "", status: "Ativo", admin_name: "", admin_email: "", admin_password: "", limits: EMPTY_LIMITS };
const STATUS_STYLE = { Ativo: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30", Suspenso: "bg-amber-500/10 text-amber-600 border-amber-500/30", Bloqueado: "bg-rose-500/10 text-rose-600 border-rose-500/30" };

export default function Tenants() {
  const { setTenantId } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/tenants").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ ...EMPTY, ...t, limits: { ...EMPTY_LIMITS, ...(t.limits || {}) } }); setOpen(true); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });
  const setLimit = (k) => (e) => setForm({ ...form, limits: { ...form.limits, [k]: Math.max(0, parseInt(e.target.value || "0", 10) || 0) } });

  const uploadLogo = async (file) => {
    if (!file || !editing) return;
    const data = await new Promise((res) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.readAsDataURL(file); });
    try {
      const r = await api.post(`/tenants/${editing.id}/logo`, { data, mimetype: file.type, filename: file.name });
      setEditing(r.data); setForm((f) => ({ ...f, logo_url: r.data.logo_url })); toast.success("Logo atualizada"); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const removeLogo = async () => {
    try { const r = await api.delete(`/tenants/${editing.id}/logo`); setEditing(r.data); setForm((f) => ({ ...f, logo_url: null })); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const submit = async () => {
    if (!form.razao_social || !form.nome_fantasia) return toast.error("Razão social e nome fantasia são obrigatórios");
    try {
      if (editing) {
        const { admin_name, admin_email, admin_password, users_count, orders_count, open_orders, channels_count, limits_usage, logo_url, logo_file_id, logo_updated_at, id, created_at, ...rest } = form;
        await api.put(`/tenants/${editing.id}`, rest);
        toast.success("Empresa atualizada");
      } else {
        const payload = { ...form };
        if (!payload.admin_email) { delete payload.admin_email; delete payload.admin_password; }
        await api.post("/tenants", payload);
        toast.success("Empresa criada");
      }
      setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async (t) => {
    if (!window.confirm(`Excluir empresa "${t.nome_fantasia}" e TODOS os seus dados?`)) return;
    await api.delete(`/tenants/${t.id}`); toast.success("Empresa excluída"); load();
  };

  const enter = (t) => { setTenantId(t.id); navigate("/"); };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5 sm:space-y-6">
      <PageHeader title="Empresas (Tenants)" subtitle="Cada empresa possui ambiente totalmente isolado: usuários, filas, canais e OS.">
        <Button onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-tenant-btn"><Plus className="w-4 h-4 mr-2" /> Nova empresa</Button>
      </PageHeader>

      <Card className="border-slate-200 dark:border-slate-800 overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
            <tr><Th>Empresa</Th><Th>CNPJ</Th><Th>Contato</Th><Th>Limites contratados</Th><Th>OS (abertas/total)</Th><Th>Status</Th><Th>Criada</Th><Th right>Ações</Th></tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
            {rows.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-sm text-slate-500">Nenhuma empresa cadastrada.</td></tr>}
            {rows.map((t) => (
              <tr key={t.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50" data-testid={`tenant-row-${t.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center overflow-hidden">
                      {t.logo_url ? <img src={t.logo_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${t.logo_url}` : t.logo_url} alt="" className="w-full h-full object-cover" /> : <Building className="w-4 h-4 text-emerald-600" />}
                    </div>
                    <div><p className="font-semibold text-sm text-slate-900 dark:text-white">{t.nome_fantasia}</p><p className="text-xs text-slate-500">{t.razao_social}</p></div>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-slate-600 font-mono">{t.cnpj || "—"}</td>
                <td className="px-4 py-3 text-xs text-slate-600"><p>{t.email || "—"}</p><p>{t.phone}</p></td>
                <td className="px-4 py-3"><LimitsBar usage={t.limits_usage} keys={["max_users", "max_channels", "max_connections"]} compact testId={`limits-${t.id}`} /></td>
                <td className="px-4 py-3 text-sm"><span className="font-semibold text-amber-600">{t.open_orders}</span> / {t.orders_count}</td>
                <td className="px-4 py-3"><Badge variant="outline" className={STATUS_STYLE[t.status]}>{t.status}</Badge></td>
                <td className="px-4 py-3 text-xs text-slate-500">{fmtDate(t.created_at)}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex gap-1 justify-end">
                    <Button size="sm" variant="outline" onClick={() => enter(t)} data-testid={`enter-tenant-${t.id}`}><LogIn className="w-3.5 h-3.5 mr-1" /> Acessar</Button>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(t)} data-testid={`edit-tenant-${t.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => del(t)} data-testid={`delete-tenant-${t.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white dark:bg-slate-900 sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar empresa" : "Nova empresa"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <p className="col-span-2 text-xs font-bold uppercase tracking-wider text-slate-500">1. Dados da empresa</p>
            <div className="col-span-2"><Label>Razão Social *</Label><Input data-testid="tenant-razao-input" value={form.razao_social} onChange={set("razao_social")} /></div>
            <div><Label>Nome Fantasia *</Label><Input data-testid="tenant-fantasia-input" value={form.nome_fantasia} onChange={set("nome_fantasia")} /></div>
            <div><Label>CNPJ</Label><Input data-testid="tenant-cnpj-input" value={form.cnpj} onChange={set("cnpj")} placeholder="00.000.000/0000-00" /></div>
            <div><Label>Telefone</Label><Input value={form.phone} onChange={set("phone")} /></div>
            <div><Label>E-mail</Label><Input value={form.email} onChange={set("email")} /></div>
            <div className="col-span-2"><Label>Endereço</Label><Input value={form.address} onChange={set("address")} /></div>
            <div><Label>Cidade</Label><Input value={form.city} onChange={set("city")} /></div>
            <div><Label>Estado</Label><Input value={form.state} onChange={set("state")} maxLength={2} placeholder="UF" /></div>
            <div className="col-span-2"><Label>Logo</Label>
              {editing ? (
                <div className="flex items-center gap-3 mt-1">
                  <div className="w-14 h-14 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 flex items-center justify-center overflow-hidden">
                    {form.logo_url ? <img src={form.logo_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${form.logo_url}` : form.logo_url} alt="" className="w-full h-full object-cover" data-testid="tenant-logo-preview" /> : <Building className="w-5 h-5 text-slate-400" />}
                  </div>
                  <label className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-slate-300 dark:border-slate-700 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800">
                    <Upload className="w-4 h-4" /> Enviar imagem
                    <input type="file" accept="image/*" className="hidden" data-testid="tenant-logo-input" onChange={(e) => uploadLogo(e.target.files?.[0])} />
                  </label>
                  {form.logo_url && <Button variant="ghost" size="sm" onClick={removeLogo} data-testid="tenant-logo-remove"><X className="w-4 h-4 mr-1" /> Remover</Button>}
                </div>
              ) : <p className="text-xs text-slate-500 mt-1">Salve a empresa primeiro para enviar a logo.</p>}
            </div>
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={set("status")}>
                <SelectTrigger data-testid="tenant-status-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white dark:bg-slate-900">{["Ativo", "Suspenso", "Bloqueado"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2 mt-2 p-3 rounded-lg border border-slate-200 dark:border-slate-700 space-y-3" data-testid="tenant-limits-section">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">2. Limites da empresa <span className="font-normal normal-case tracking-normal">(0 = ilimitado)</span></p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {Object.entries(LIMIT_META).map(([k, m]) => (
                  <div key={k}><Label className="text-xs">Máx. {m.label}</Label>
                    <Input type="number" min={0} data-testid={`tenant-limit-${k}`} value={form.limits?.[k] ?? 0} onChange={setLimit(k)} />
                    {editing?.limits_usage?.[k] && <p className={`text-[11px] mt-1 ${editing.limits_usage[k].reached ? "text-rose-600" : "text-slate-500"}`}>em uso: {editing.limits_usage[k].used}</p>}
                  </div>
                ))}
              </div>
            </div>
            {!editing && (
              <div className="col-span-2 mt-2 p-3 rounded-lg border border-dashed border-slate-300 dark:border-slate-700 space-y-3">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Administrador inicial (opcional)</p>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Nome</Label><Input data-testid="tenant-admin-name-input" value={form.admin_name} onChange={set("admin_name")} /></div>
                  <div><Label>E-mail</Label><Input data-testid="tenant-admin-email-input" value={form.admin_email} onChange={set("admin_email")} /></div>
                  <div><Label>Senha</Label><Input data-testid="tenant-admin-password-input" type="password" value={form.admin_password} onChange={set("admin_password")} /></div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={submit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-tenant-btn">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
